import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { LocaleService } from './locale.service';
import { ProfileService } from './openapi';
import { PersonalRolesDto } from './openapi/model/models';

interface RoleState {
  roles: PersonalRolesDto | null;
  loading: boolean;
  error: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RoleStateService {
  private readonly msalService: MsalService = inject(MsalService);
  private readonly localeService: LocaleService = inject(LocaleService);
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly roleCache = new Map<string, PersonalRolesDto>();
  private readonly inFlightRequests = new Map<string, Observable<PersonalRolesDto>>();
  private readonly roleStateSubject = new BehaviorSubject<RoleState>({
    roles: null,
    loading: false,
    error: false,
  });

  public readonly rolesState$ = this.roleStateSubject.asObservable();

  ensureRolesLoaded(forceRefresh: boolean = false): Observable<PersonalRolesDto> {
    const cacheKey = this.getCacheKey();

    if (!forceRefresh) {
      const cachedRoles = this.roleCache.get(cacheKey);
      if (cachedRoles) {
        this.roleStateSubject.next({ roles: cachedRoles, loading: false, error: false });
        return of(cachedRoles);
      }

      const inFlightRequest = this.inFlightRequests.get(cacheKey);
      if (inFlightRequest) {
        return inFlightRequest;
      }
    }

    this.roleStateSubject.next({
      roles: this.roleCache.get(cacheKey) ?? null,
      loading: true,
      error: false,
    });

    const request$ = this.profileService.getPersonalRoles(this.localeService.regionCode).pipe(
      tap((roles) => {
        this.roleCache.set(cacheKey, roles);
        this.roleStateSubject.next({ roles, loading: false, error: false });
      }),
      catchError((error) => {
        this.roleStateSubject.next({ roles: null, loading: false, error: true });
        return throwError(() => error);
      }),
      finalize(() => {
        this.inFlightRequests.delete(cacheKey);
      }),
      shareReplay(1)
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }

  isEvaluator(): boolean {
    return !!this.getRoleSnapshot()?.isEvaluator;
  }

  clearCurrent(): void {
    const cacheKey = this.getCacheKey();
    this.roleCache.delete(cacheKey);
    this.inFlightRequests.delete(cacheKey);
    this.roleStateSubject.next({ roles: null, loading: false, error: false });
  }

  clearAll(): void {
    this.roleCache.clear();
    this.inFlightRequests.clear();
    this.roleStateSubject.next({ roles: null, loading: false, error: false });
  }

  private getRoleSnapshot(): PersonalRolesDto | null {
    return this.roleCache.get(this.getCacheKey()) ?? this.roleStateSubject.value.roles;
  }

  private getCacheKey(): string {
    const activeAccount: AccountInfo | null = this.msalService.instance.getActiveAccount();
    const accountKey = activeAccount?.homeAccountId ?? activeAccount?.localAccountId ?? 'anonymous';

    return `${accountKey}:${this.localeService.regionCode}`;
  }
}
